/**
 * useConversationHistory Hook
 *
 * Hook for managing AI conversation history - list, load, save, and archive.
 */
import { useState, useCallback, useEffect } from 'react';
import { trpc } from '@/trpc/client';
import type { ColleagueId, ContextType, Message } from './useAIColleague';

export type Conversation = {
  id: string;
  operatorId: string;
  colleagueId: string;
  title: string | null;
  contextType: string | null;
  contextId: string | null;
  createdAt: Date;
  updatedAt: Date;
  archivedAt: Date | null;
};

export type ConversationWithMessages = Conversation & {
  messages: Array<{
    id: string;
    conversationId: string;
    role: string;
    content: string;
    toolCalls: unknown;
    toolResults: unknown;
    createdAt: Date;
  }>;
};

export type UseConversationHistoryReturn = {
  /** List of conversations */
  conversations: Conversation[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Refresh the conversation list */
  refresh: () => Promise<void>;
  /** Load a specific conversation */
  loadConversation: (id: string) => Promise<ConversationWithMessages | null>;
  /** Create a new conversation */
  createConversation: (params: {
    colleagueId: ColleagueId;
    title?: string;
    contextType?: ContextType;
    contextId?: string;
  }) => Promise<string>;
  /** Save messages to a conversation */
  saveMessages: (conversationId: string, messages: Message[]) => Promise<void>;
  /** Update conversation title */
  updateTitle: (id: string, title: string) => Promise<void>;
  /** Archive a conversation */
  archiveConversation: (id: string) => Promise<void>;
  /** Generate title from first message */
  generateTitle: (conversationId: string, firstMessage: string) => Promise<string>;
  /** Current active conversation ID */
  activeConversationId: string | null;
  /** Set active conversation */
  setActiveConversationId: (id: string | null) => void;
};

export function useConversationHistory(): UseConversationHistoryReturn {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);

  // tRPC mutations
  const listMutation = trpc.aiColleagues.listConversations.useMutation();
  const getConversationMutation = trpc.aiColleagues.getConversation.useMutation();
  const createMutation = trpc.aiColleagues.createConversation.useMutation();
  const addMessagesMutation = trpc.aiColleagues.addMessages.useMutation();
  const updateTitleMutation = trpc.aiColleagues.updateConversationTitle.useMutation();
  const archiveMutation = trpc.aiColleagues.archiveConversation.useMutation();
  const generateTitleMutation = trpc.aiColleagues.generateTitle.useMutation();

  // Refresh conversation list
  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await listMutation.mutateAsync({
        limit: 50,
        offset: 0,
        includeArchived: false
      });
      setConversations(result.conversations.map(c => ({
        ...c,
        createdAt: new Date(c.createdAt),
        updatedAt: new Date(c.updatedAt),
        archivedAt: c.archivedAt ? new Date(c.archivedAt) : null
      })));
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
    // listMutation excluded: mutateAsync is stable, including it causes infinite re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load on mount
  useEffect(() => {
    refresh();
  }, []);

  // Load a specific conversation with messages
  const loadConversation = useCallback(async (id: string): Promise<ConversationWithMessages | null> => {
    try {
      const result = await getConversationMutation.mutateAsync({ id, messageLimit: 100 });
      if (!result) return null;
      return {
        ...result,
        createdAt: new Date(result.createdAt),
        updatedAt: new Date(result.updatedAt),
        archivedAt: result.archivedAt ? new Date(result.archivedAt) : null,
        messages: result.messages.map(m => ({
          ...m,
          createdAt: new Date(m.createdAt)
        }))
      };
    } catch {
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create a new conversation
  const createConversation = useCallback(async (params: {
    colleagueId: ColleagueId;
    title?: string;
    contextType?: ContextType;
    contextId?: string;
  }): Promise<string> => {
    const result = await createMutation.mutateAsync({
      colleagueId: params.colleagueId,
      title: params.title,
      contextType: params.contextType,
      contextId: params.contextId
    });
    // Refresh list
    await refresh();
    return result.id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  // Save messages to a conversation
  const saveMessages = useCallback(async (conversationId: string, messages: Message[]) => {
    // Filter to only save messages that don't have IDs starting with the conversationId
    // (to avoid re-saving already saved messages)
    const messagesToSave = messages.map(m => ({
      role: m.role as 'user' | 'assistant' | 'tool' | 'system',
      content: m.content,
      toolCalls: m.toolCalls,
      toolResults: m.toolResults
    }));

    if (messagesToSave.length === 0) return;

    await addMessagesMutation.mutateAsync({
      conversationId,
      messages: messagesToSave
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update conversation title
  const updateTitle = useCallback(async (id: string, title: string) => {
    await updateTitleMutation.mutateAsync({ id, title });
    // Update local state
    setConversations(prev => prev.map(c => 
      c.id === id ? { ...c, title, updatedAt: new Date() } : c
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Archive a conversation
  const archiveConversation = useCallback(async (id: string) => {
    await archiveMutation.mutateAsync({ id });
    // Remove from local state
    setConversations(prev => prev.filter(c => c.id !== id));
    setActiveConversationId(prev => prev === id ? null : prev);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Generate title from first message
  const generateTitle = useCallback(async (conversationId: string, firstMessage: string): Promise<string> => {
    const result = await generateTitleMutation.mutateAsync({ conversationId, firstMessage });
    // Update local state
    setConversations(prev => prev.map(c => 
      c.id === conversationId ? { ...c, title: result.title, updatedAt: new Date() } : c
    ));
    return result.title;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    conversations,
    isLoading,
    error,
    refresh,
    loadConversation,
    createConversation,
    saveMessages,
    updateTitle,
    archiveConversation,
    generateTitle,
    activeConversationId,
    setActiveConversationId
  };
}
